import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * Galleries the server has said are blocked, newer than whatever a screen still holds (U-15b).
 *
 * A list page or an open gallery window may hold an answer from before the policy changed: a card with a
 * cover, a detail that allowed downloading. When a later answer says "blocked" (a detail without a cover
 * address, or `POLICY_BLOCKED` from the download or reader API), the gallery is noted here, and every place
 * that draws a cover or offers downloading or reading asks here first. The newest refusal wins over older
 * data. Nothing is ever taken off the set: it fails closed until the page is loaded again.
 *
 * Only server answers put a gallery here; the client still never looks at tags.
 */
interface ServerBlocksApi {
  isBlocked(gid: number): boolean;
  /** the server just answered that this gallery is blocked */
  note(gid: number): void;
}

const Ctx = createContext<ServerBlocksApi | null>(null);

export function ServerBlocksProvider({ children }: { children: ReactNode }) {
  const [gids, setGids] = useState<ReadonlySet<number>>(() => new Set());
  const note = useCallback(
    (gid: number) => setGids((prev) => (prev.has(gid) ? prev : new Set(prev).add(gid))),
    [],
  );
  const api = useMemo<ServerBlocksApi>(() => ({ isBlocked: (gid) => gids.has(gid), note }), [gids, note]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useServerBlocks(): ServerBlocksApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useServerBlocks outside ServerBlocksProvider");
  return v;
}
